import { css, SerializedStyles } from '@emotion/react';
import { useMemo } from 'react';
import { createResponsiveStyles, ResponsiveStyleValue, useHoneycombTheme } from '../../../theming';
import { CarouselButtonSize } from '../Carousel.types';

// Static styles
export const STATIC_STYLES: SerializedStyles = css`
    --cb-size-l: 48px;
    --cb-icon-size-l: 24px;

    --cb-size-m: 40px;
    --cb-icon-size-m: 20px;

    --cb-size-s: 32px;
    --cb-icon-size-s: 16px;

    --cb-light-bg-color: #fff;
    --cb-light-border-color: var(--hc-color-border);
    --cb-light-icon-color: #000;

    --cb-dark-bg-color: #000;
    --cb-dark-border-color: #fff;
    --cb-dark-icon-color: #fff;

    position: relative;
    cursor: pointer;
    pointer-events: all;

    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: var(--cb-size);
    height: var(--cb-size);
    border-radius: 50%;

    background-color: var(--cb-bg-color);

    > svg {
        width: var(--cb-icon-size);
        height: var(--cb-icon-size);
        position: relative; /* above pseudo-element */
        z-index: 2;
        fill: var(--cb-icon-color);
    }

    &:where([data-border]) {
        border: 1px solid var(--cb-border-color);
    }

    &:hover {
        box-shadow: var(--hc-shadow-s);
    }

    &:focus-visible {
        outline: 2px solid var(--hc-color-action-focus);
    }

    &:where([data-hidden-for-touch]) {
        @media (pointer: coarse) {
            display: none;
        }
    }

    opacity: 0.9;
    transition: opacity 0.2s ease;

    &:not([disabled]):hover {
        opacity: 1;
    }
`;

export function useDynamicStyles(
    color: 'light' | 'dark',
    size: ResponsiveStyleValue<CarouselButtonSize>
): React.CSSProperties {
    const { theme } = useHoneycombTheme();

    const styles = useMemo(() => {
        return {
            '--cb-bg-color': `var(--cb-${color}-bg-color)`,
            '--cb-border-color': `var(--cb-${color}-border-color)`,
            '--cb-icon-color': `var(--cb-${color}-icon-color)`,
            ...createResponsiveStyles(
                [
                    {
                        value: size,
                        getStyles: (value: CarouselButtonSize) => {
                            return {
                                '--cb-size': `var(--cb-size-${value})`,
                                '--cb-icon-size': `var(--cb-icon-size-${value})`,
                            };
                        },
                    },
                ],
                theme
            ),
        } as React.CSSProperties;
    }, [color, size, theme]);

    return styles;
}
